import { useQuery } from '@tanstack/react-query';
import { Flag, Icon, type IconName, SignalMeter, Skeleton } from '@uplink/ui';
import { useEffect, useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { CURRENCY_SIGNS, type Currency, usePrefs } from '../prefs.tsx';
import { runwayProps } from '../runway.ts';
import { serverState } from '../servers-ui.tsx';
import { haptic } from '../telegram.ts';

const ROWS = 5;

export function useWide(query = '(min-width: 960px)') {
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return wide;
}

function Action({
  icon,
  title,
  sub,
  onClick,
}: {
  icon: IconName;
  title: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="dk-action" onClick={onClick}>
      <span className="dk-action__icon">
        <Icon name={icon} size={20} />
      </span>
      <span className="dk-action__text">
        <b>{title}</b>
        <small>{sub}</small>
      </span>
      <Icon name="chevron" size={16} className="dk-action__go" />
    </button>
  );
}

/**
 * Home on a wide screen: the balance with its forecast, what to create next, and the servers and domains
 * as tables. The phone layout stays the Mini App's.
 */
export function DesktopHome({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const prefs = usePrefs();
  const { t, money, date, currency } = prefs;
  const me = useQuery(trpc.me.queryOptions());
  const balance = useQuery(trpc.balance.summary.queryOptions());
  const domains = useQuery(trpc.domains.list.queryOptions());
  const servers = useQuery(trpc.servers.list.queryOptions());
  const config = useQuery(trpc.config.queryOptions());
  const runway = balance.data ? runwayProps(balance.data, { money, date, t }) : {};

  return (
    <main className="dk">
      <header className="dk-top">
        <div>
          <h1>{me.data ? t.home.greeting(me.data.firstName) : ' '}</h1>
          <p>{t.desk.subtitle}</p>
        </div>
        <div className="dk-top__actions">
          <button type="button" className="dk-btn dk-btn--ghost" onClick={() => nav.tab('balance')}>
            <Icon name="receipt" size={18} />
            {t.desk.history}
          </button>
          <button
            type="button"
            className="dk-btn dk-btn--primary"
            onClick={() => nav.push({ name: 'topup' })}
          >
            <Icon name="plus" size={18} />
            {t.desk.topUp}
          </button>
        </div>
      </header>

      <div className="dk-grid">
        <section className="dk-card dk-balance" aria-label={t.desk.balance}>
          {balance.isPending ? (
            <Skeleton height={150} />
          ) : balance.isError ? (
            <div className="ul-error" role="alert">
              {errorText(balance.error)}
            </div>
          ) : (
            <>
              <div className="dk-balance__head">
                <span className="dk-label">{t.desk.balance}</span>
                <div className="dk-currency" role="radiogroup" aria-label={t.profile.currency}>
                  {(Object.keys(CURRENCY_SIGNS) as Currency[]).map((code) => (
                    // biome-ignore lint/a11y/useSemanticElements: компактный переключатель использует паттерн WAI-ARIA radio
                    <button
                      key={code}
                      type="button"
                      role="radio"
                      aria-checked={currency === code}
                      title={code}
                      onClick={() => {
                        haptic.select();
                        prefs.setCurrency(code);
                      }}
                    >
                      {CURRENCY_SIGNS[code]}
                    </button>
                  ))}
                </div>
              </div>
              <b className="dk-amount">{money(balance.data.balanceCents)}</b>
              <dl className="dk-meta">
                <div>
                  <dt>{t.desk.monthly}</dt>
                  <dd>{runway.monthly ?? '—'}</dd>
                </div>
                <div>
                  <dt>{t.desk.runway}</dt>
                  <dd className={runway.runwayTone ? `is-${runway.runwayTone}` : undefined}>
                    {runway.runway ?? '—'}
                  </dd>
                </div>
              </dl>
              <span className="dk-note">
                <Icon name="bolt" size={15} />
                {t.home.payVia(
                  (config.data?.paymentMethods ?? ['cryptopay']).map((m) =>
                    m === 'xrocket' ? '@xRocket' : '@CryptoBot',
                  ),
                )}
              </span>
            </>
          )}
        </section>

        <section className="dk-card dk-actions" aria-label={t.desk.create}>
          <span className="dk-label">{t.desk.create}</span>
          <div className="dk-actions__grid">
            <Action
              icon="globe"
              title={t.home.domain}
              sub={
                config.data?.domainFromCents
                  ? t.home.domainFrom(money(config.data.domainFromCents))
                  : t.home.domainHint
              }
              onClick={() => nav.tab('domains')}
            />
            <Action
              icon="server"
              title={t.home.server}
              sub={
                config.data?.serverFromCents
                  ? t.home.serverFrom(money(config.data.serverFromCents))
                  : t.home.serverHint
              }
              onClick={() => nav.push({ name: 'serverCreate' })}
            />
            <Action
              icon="shield"
              title={t.servers.createVpn}
              sub={t.desk.vpnSub}
              onClick={() => nav.push({ name: 'serverCreate', vpn: true })}
            />
            {config.data?.sharedVpn.available ? (
              <Action
                icon="key"
                title={t.sharedVpn.shortTitle}
                sub={t.sharedVpn.from(money(config.data.sharedVpn.priceMonthly))}
                onClick={() => nav.push({ name: 'vpnSubscription' })}
              />
            ) : null}
          </div>
        </section>
      </div>

      <section className="dk-card dk-table" aria-labelledby="dk-servers">
        <header className="dk-table__head">
          <h2 id="dk-servers">
            {t.home.myServers}
            {servers.data?.length ? <span>{servers.data.length}</span> : null}
          </h2>
          <button type="button" className="dk-link" onClick={() => nav.push({ name: 'serverCreate' })}>
            <Icon name="plus" size={16} />
            {t.servers.create}
          </button>
        </header>
        {servers.isPending ? (
          <Skeleton height={120} />
        ) : servers.data?.length ? (
          <div className="dk-rows">
            {servers.data.slice(0, ROWS).map((s) => {
              const st = serverState(s, t);
              return (
                <button
                  key={s.id}
                  type="button"
                  className="dk-row dk-row--server"
                  onClick={() => nav.push({ name: 'server', id: s.id })}
                >
                  <span className="dk-row__name">
                    <Flag country={s.country} size={24} />
                    <b>{s.name}</b>
                  </span>
                  <span className="dk-muted">
                    {s.city} · {s.spec}
                  </span>
                  <span className="dk-mono">{s.ipv4 ?? '—'}</span>
                  <span className="dk-status">
                    <SignalMeter state={st.state} label={st.label} />
                    {st.label}
                  </span>
                  <Icon name="chevron" size={16} className="dk-row__go" />
                </button>
              );
            })}
            {servers.data.length > ROWS ? (
              <button type="button" className="dk-more" onClick={() => nav.tab('servers')}>
                {t.common.allN(servers.data.length)}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="dk-empty">
            <Icon name="server" size={22} />
            <span>{t.desk.noServers}</span>
          </div>
        )}
      </section>

      <section className="dk-card dk-table" aria-labelledby="dk-domains">
        <header className="dk-table__head">
          <h2 id="dk-domains">
            {t.home.myDomains}
            {domains.data?.length ? <span>{domains.data.length}</span> : null}
          </h2>
          <button type="button" className="dk-link" onClick={() => nav.tab('domains')}>
            <Icon name="search" size={16} />
            {t.desk.findDomain}
          </button>
        </header>
        {domains.isPending ? (
          <Skeleton height={100} />
        ) : domains.data?.length ? (
          <div className="dk-rows">
            {domains.data.slice(0, ROWS).map((d) => (
              <button
                key={d.id}
                type="button"
                className="dk-row dk-row--domain"
                onClick={() => nav.push({ name: 'domain', id: d.id })}
              >
                <span className="dk-row__name">
                  <span className="dk-row__icon">
                    <Icon name="globe" size={16} />
                  </span>
                  <b className="dk-mono">{d.name}</b>
                </span>
                <span className="dk-muted">
                  {d.status === 'active'
                    ? t.common.until(d.expiresAt ? date(d.expiresAt) : '—')
                    : d.status === 'expired'
                      ? t.domains.expiredRenew
                      : t.domains.registering}
                </span>
                <span className={`dk-pill${d.autoRenew ? ' is-on' : ''}`}>
                  {d.autoRenew ? t.desk.autoOn : t.desk.autoOff}
                </span>
                <Icon name="chevron" size={16} className="dk-row__go" />
              </button>
            ))}
            {domains.data.length > ROWS ? (
              <button type="button" className="dk-more" onClick={() => nav.tab('domains')}>
                {t.common.allN(domains.data.length)}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="dk-empty">
            <Icon name="globe" size={22} />
            <span>{t.desk.noDomains}</span>
          </div>
        )}
      </section>
    </main>
  );
}
